import { partsQuery } from "@common/api/parts"
import type { ControlSize } from "@common/components/ui/control"
import {
  FormField,
  FormItem,
  FormMessage,
  useFormField,
} from "@common/components/ui/form"
import { useDebouncedValue } from "@common/hooks/use-debounced-value"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form"
import { useFormContext } from "react-hook-form"
import { FieldLabel } from "../fields/label"
import { LookupInput, type LookupOption } from "../fields/lookup-input"
import {
  type ExistingPartValue,
  type PartRefValue,
  partLabel,
} from "./part-value"

const DEBOUNCE_MS = 200

type Props<T extends FieldValues> = {
  name: Path<T>
  label?: string
  className?: string
  disabled?: boolean
  optional?: boolean
  clearable?: boolean
  onPick?: (part: ExistingPartValue, description: string | null) => void
  onClear?: () => void
  onCreate?: (number: string) => void
}

export function BarePartField<T extends FieldValues>({
  name,
  className,
  size,
  ariaLabel = "Part",
  disabled,
  clearable,
  onPick,
  onClear,
  onCreate,
}: Omit<Props<T>, "label" | "optional"> & {
  ariaLabel?: string
  size?: ControlSize
}): React.ReactElement {
  const { control } = useFormContext<T>()

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <PartLookup
            field={field}
            className={className}
            size={size}
            ariaLabel={ariaLabel}
            disabled={disabled}
            clearable={clearable}
            onPick={onPick}
            onClear={onClear}
            onCreate={onCreate}
          />
        </FormItem>
      )}
    />
  )
}

export function PartField<T extends FieldValues>({
  name,
  label = "Part",
  className,
  disabled,
  optional,
  clearable,
  onPick,
  onClear,
  onCreate,
}: Props<T>): React.ReactElement {
  const { control } = useFormContext<T>()

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FieldLabel label={label} optional={optional} />
          <PartLookup
            field={field}
            disabled={disabled}
            clearable={clearable}
            onPick={onPick}
            onClear={onClear}
            onCreate={onCreate}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

function PartLookup<T extends FieldValues>({
  field,
  className,
  size,
  ariaLabel,
  disabled,
  clearable,
  onPick,
  onClear,
  onCreate,
}: {
  field: ControllerRenderProps<T, Path<T>>
  className?: string
  size?: ControlSize
  ariaLabel?: string
  disabled?: boolean
  clearable?: boolean
  onPick?: (part: ExistingPartValue, description: string | null) => void
  onClear?: () => void
  onCreate?: (number: string) => void
}): React.ReactElement {
  const { formItemId, formMessageId, error } = useFormField()
  const value: PartRefValue | null = field.value ?? null
  const [typed, setTyped] = useState<string | null>(null)
  const search = typed ?? (value ? partLabel(value) : "")

  const term = useDebouncedValue(search, DEBOUNCE_MS)
  const { data } = useQuery({
    ...partsQuery({ search: term, limit: 10 }),
    enabled: term.trim().length > 0,
  })

  const options: LookupOption[] = (data?.data ?? []).map((part) => ({
    id: part.id,
    label: part.number,
    hint: part.description,
  }))

  return (
    <LookupInput
      search={search}
      options={options}
      disabled={disabled}
      placeholder="Search part numbers"
      className={className}
      size={size}
      aria-label={ariaLabel}
      id={formItemId}
      aria-describedby={error ? formMessageId : undefined}
      aria-invalid={error ? true : undefined}
      onSearch={setTyped}
      onPick={(option) => {
        const picked = { id: option.id, number: option.label }
        setTyped(null)
        field.onChange(picked)
        onPick?.(picked, option.hint ?? null)
      }}
      onCreate={onCreate && (() => onCreate(search.trim()))}
      onBlur={() => {
        if (clearable && value !== null && typed?.trim() === "") {
          field.onChange(null)
          onClear?.()
        }
        setTyped(null)
        field.onBlur()
      }}
    />
  )
}
